import { useId, type ReactNode } from 'react';
import { GuidanceButton, GuidancePanel } from './FieldGuidance';
import { useFieldGuidance } from './useFieldGuidance';
import { REQUIREMENT_LABEL, fieldDef, type RoleFieldDefinition } from './roleFieldRegistry';

/** What a control needs so it is wired to its label, guidance, hint, counter and error. */
export interface ControlA11y {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
  'aria-required': true | undefined;
  'data-field': string;
}

export interface GuidedFieldProps {
  /** The registry key (or the definition itself). The label, badge and guidance all come from it. */
  field: string | RoleFieldDefinition;
  error?: string | undefined;
  /** Extra hint text under the control. */
  hint?: ReactNode | undefined;
  /** A live character count for a text control: "123 of 500 characters". */
  counter?: { length: number; max?: number | undefined } | undefined;
  /** `control` (default): the label names the control (`htmlFor`). `group`: the label names a group of controls (fieldset-like). */
  labelMode?: 'control' | 'group' | undefined;
  /** Extra classes for the wrapper, for example `jr-grid__wide`. */
  className?: string | undefined;
  /** Replaces the registry label for display (the guidance and badge stay the same). Rarely needed. */
  labelText?: string | undefined;
  /** Content shown at the end of the label row (for example a "Suggest a code" button). */
  headExtra?: ReactNode | undefined;
  /** Content shown inside the guidance panel under the registry text. */
  guidanceExtra?: ReactNode | undefined;
  /** Disable the Required/Optional badge (the Review step shows requirement in its own lists). */
  hideBadge?: boolean | undefined;
  children: (a11y: ControlA11y) => ReactNode;
}

/**
 * A field of the wizard: label, a Required / Recommended / Optional badge (words, never colour alone), the guidance button and panel, the
 * control, a hint, a character counter and the error, all wired with ids, `aria-describedby`, `aria-invalid` and `aria-required`.
 * Everything that is wording comes from the registry, so a field's text is changed in one place.
 */
export function GuidedField({ field, error, hint, counter, labelMode = 'control', className, labelText, headExtra, guidanceExtra, hideBadge, children }: GuidedFieldProps) {
  const def = typeof field === 'string' ? fieldDef(field) : field;
  const uid = useId();
  const id = `${uid}-${def.key}`;
  const { state: guidance, setButton } = useFieldGuidance();
  const hintId = hint ? `${id}-hint` : undefined;
  const counterId = counter ? `${id}-count` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId, counterId, guidance.panelId].filter(Boolean).join(' ') || undefined;
  const a11y: ControlA11y = {
    id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined,
    'aria-required': def.requirement === 'required' ? true : undefined, 'data-field': def.key,
  };
  const label = labelText ?? def.label;
  const labelId = `${id}-label`;
  const over = counter?.max !== undefined && counter.length > counter.max;

  return (
    <div className={['jr-field', 'jr-gfield', className].filter(Boolean).join(' ')} onKeyDown={guidance.onKeyDown} data-field-wrapper={def.key}>
      <div className="jr-fieldhead">
        {labelMode === 'control'
          ? <label id={labelId} htmlFor={id}>{label}</label>
          : <span id={labelId} className="jr-fieldhead__label">{label}</span>}
        {!hideBadge && <span className={`jr-badge jr-badge--req jr-badge--${def.requirement}`}>{REQUIREMENT_LABEL[def.requirement]}</span>}
        <GuidanceButton field={def} state={guidance} buttonRef={setButton} />
        {headExtra}
      </div>
      <GuidancePanel field={def} state={guidance} extra={guidanceExtra} />
      {labelMode === 'group' ? <div role="group" aria-labelledby={labelId} aria-describedby={describedBy}>{children(a11y)}</div> : children(a11y)}
      {hint && <p id={hintId} className="jr-hint">{hint}</p>}
      {counter && (
        <p id={counterId} className={over ? 'jr-hint jr-hint--over' : 'jr-hint'}>
          {counter.length}{counter.max !== undefined ? ` of ${counter.max}` : ''} characters
          {def.recommendedLength?.unit === 'characters' ? `. ${def.recommendedLength.text}` : ''}
        </p>
      )}
      {error && <p id={errorId} className="jr-error" role="alert">{error}</p>}
    </div>
  );
}
